import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip
} from "reactstrap";
import { Link } from "react-router-dom";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import getAccessRights from "components/Common/getAccessRights";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomSelect from "components/Common/custom-select";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeDeductionStatus, fetchDeductions, createDeductionRequest } from "store/actions";
import Swal from "sweetalert2";
import Loader from "common/pageLoader";

const PayrollDeductions = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);

  const {
    deductions,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    deductions: state.PayrollSetupDeductionReducer?.deductions ?? [],

    loading: state.PayrollSetupDeductionReducer?.loading,
    error: state.PayrollSetupDeductionReducer?.error,
    success: state.PayrollSetupDeductionReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchDeductions(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchDeductions(authDetails?.unit_id));
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const accessRight = getAccessRights(location.pathname, history);
  }, [location]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      deduction: "",
      description: "",
      type: null, // ReactSelect requires object format
      is_tds: false,
      is_nonsalary: false,
      visibility: false,
      unitid: authDetails?.unit_id,
      createdby: authDetails?.user_id,
    },
    validationSchema: Yup.object({
      deduction: Yup.string().required("Deduction is required").matches(/^\S.*\S$|^\S$/, "Leading and trailing spaces are not allowed"),
      description: Yup.string().required("Description is required"),
      type: Yup.object().nullable().required("Type is required"),
    }),
    onSubmit: (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("pay_ded_id", editId ? editId : 0);
      formData.append("ded_name", values.deduction);
      formData.append("ded_desc", values.description);
      formData.append("ded_type", values.type.value);
      formData.append("is_tds", values.is_tds ? 1 : 0);
      formData.append("is_nonsalary", values.is_nonsalary ? 1 : 0);
      formData.append("is_visible", values.visibility ? 1 : 0);
      formData.append("create_by", authDetails?.user_id);
      formData.append("unit_id", authDetails?.unit_id);
      dispatch(createDeductionRequest(formData))
    },
  });


  const handleEdit = (row) => {
    formik.setValues({
      id: row.id,
      deduction: row.ded_name,
      description: row.ded_desc,
      type: { value: row.ded_type_id, label: row.ded_type }, // React-Select expects an object      
      is_tds: row.is_tds === 1,
      is_nonsalary: row.is_nonsalary === 1,
      visibility: row.is_visible === 1,
      unitid: authDetails?.unit_id,
      createdby: authDetails?.user_id,
    });
    setIsEdit(true);
    setEditId(row.pay_ded_id);
  };

  const handleCancel = () => {
    formik.resetForm();
    setIsEdit(false);
    setEditId(null);
  };

  const columns = [
    {
      Header: "S#",
      Cell: ({ row }) => deductions.length - row.index,
      className: "serial-no text-center",
    },
    {
      Header: "DEDUCTION",
      accessor: "ded_name",
    },
    {
      Header: "DESCRIPTION",
      accessor: "ded_desc",
    },
    {
      Header: "DEDUCTION TYPE",
      accessor: "ded_type"
    },
    {
      Header: "IS_NON_SALARY",
      accessor: row => (row.is_nonsalary === 1 ? "Yes" : "No"),
    },
    {
      Header: "IS_TDS",
      accessor: row => (row.is_tds === 1 ? "Yes" : "No"),
    },
    {
      Header: "VISIBILITY",
      accessor: row => (row.is_visible === 1 ? "Yes" : "No"),
    },
    {
      Header: "CREATED_BY",
      accessor: "create_by",
    },
    {
      Header: "CREATED_DATE",
      accessor: "create_dt",
    },
    {
      Header: "ACTIONS",
      Cell: (cellProps) => {
        const { b_active, pay_ded_id } = cellProps.row.original;
        const isActive = b_active === 1; // Status 1 = Active
        return (
          <div className="text-center">
            <Link
              to="#"
              style={{ color: isActive ? "green" : "red" }}
              onClick={() => handleToggleStatus(pay_ded_id, isActive)} className="me-2"
            >
              <i
                className={`mdi ${isActive ? "mdi-check-circle" : "mdi-close-circle"} font-size-18`}
                id={`status-tooltip-${cellProps.row.id}`}
              />
              <UncontrolledTooltip placement="top" target={`status-tooltip-${cellProps.row.id}`}>
                {isActive ? "Mark Inactive" : "Mark Active"}
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-success ml-3"
              onClick={() => handleEdit(cellProps.row.original)}
            >
              <i className="bx bxs-edit font-size-18" id="edittooltip" />
              <UncontrolledTooltip placement="top" target="edittooltip">
                Edit
              </UncontrolledTooltip>
            </Link>
          </div>
        );
      },
    },
  ];

  const handleToggleStatus = (id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${isActive ? 'deactivate' : 'activate'} this deduction.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("pay_ded_id", id);
        formData.append("create_by", authDetails?.user_id);
        formData.append("unit_id", authDetails?.unit_id);
        dispatch(changeDeductionStatus(formData));
      }
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Loader loading={loading} />
        <Container fluid>
          {/* Breadcrumbs */}
          <Breadcrumbs title="Payroll / Setup" breadcrumbItem="Deductions" />

          {/* Input Section */}
          <Card>
            <CardBody>
              <Form onSubmit={formik.handleSubmit}>
                <Row className="mb-3 g-3 align-items-center">
                  {/* Deduction Field */}
                  <Col md="3">
                    <FormGroup>
                      <Label for="deduction">
                        Deduction <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="deduction"
                        name="deduction"
                        type="text"
                        maxLength={50}
                        value={formik.values.deduction}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.deduction && Boolean(formik.errors.deduction)}
                      />
                      {formik.touched.deduction && formik.errors.deduction && (
                        <div className="text-danger">{formik.errors.deduction}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Description Field */}
                  <Col md="3">
                    <FormGroup>
                      <Label for="description">
                        Description <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="description"
                        name="description"
                        maxLength={100}
                        type="text"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.description && Boolean(formik.errors.description)}
                      />
                      {formik.touched.description && formik.errors.description && (
                        <div className="text-danger">{formik.errors.description}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Type Field - React Select */}
                  <Col md="2">
                    <FormGroup>
                      <Label for="type">
                        Type <span className="text-danger">*</span>
                      </Label>
                      <CustomSelect
                        id="type"
                        name="type"
                        options={[
                          { value: 1, label: "Fixed" },
                          { value: 2, label: "Manual" },
                          { value: 3, label: "Loan" },
                        ]}
                        value={formik.values.type}
                        onChange={(selectedOption) => formik.setFieldValue("type", selectedOption)}
                        onBlur={() => formik.setFieldTouched("type", true)}
                      />
                      {formik.touched.type && formik.errors.type && (
                        <div className="text-danger">{formik.errors.type}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={4} className="d-flex align-items-center flex-wrap mt-4">
                    <Col md={4}>
                      <FormGroup check className="d-flex align-items-center gap-2">
                        <Label check>
                          <Input
                            type="checkbox"
                            id="is_nonsalary"
                            name="is_nonsalary"
                            checked={formik.values.is_nonsalary}
                            onChange={formik.handleChange}
                          />
                          Is Non Salary
                        </Label>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup check className="d-flex align-items-center gap-2">
                        <Label check>
                          <Input
                            type="checkbox"
                            id="is_tds"
                            name="is_tds"
                            checked={formik.values.is_tds}
                            onChange={formik.handleChange}
                          />
                          Is TDS
                        </Label>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup check className="d-flex align-items-center gap-2">
                        <Input
                          type="checkbox"
                          id="visibility"
                          name="visibility"
                          checked={formik.values.visibility}
                          onChange={formik.handleChange}
                        />
                        <Label for="visibility" check>Visibility</Label>
                      </FormGroup>
                    </Col>
                  </Col>


                  {/* Visibility Checkbox */}

                  {/* Submit and Cancel Buttons */}
                  <Col className="d-flex mob-center align-items-center justify-content-end">
                    <Button type="submit" className="btn btn-primary me-2 mt-2">
                      <i className="fas fa-save me-1" /> {isEdit ? "UPDATE" : "SAVE"}
                    </Button>
                    <Button
                      type="button"
                      color="secondary"
                      className="btn button-cancel me-2 mt-2"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?", text: "Unsaved clear will be lost!", icon: "warning",
                          showCancelButton: true, confirmButtonText: "Yes, Clear", cancelButtonText: "No", confirmButtonColor: "#3085d6", cancelButtonColor: "#d33"
                        }).then((result) => {
                          if (result.isConfirmed) {
                            handleCancel();
                          }
                        });
                      }}
                    >
                      <i className="fas fa-sync me-1" /> CANCEL
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>

          {/* Table Section */}
          <Card>
            <CardBody>
              <TableContainer
                columns={columns}
                data={deductions && deductions?.length > 0 ? deductions : []}
                isGlobalFilter={true}
                customPageSize={10}
              // className="custom-header-css"
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PayrollDeductions;
